import React from 'react';
import { useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import UserType from './type';
import getCreatedUpdatedColumns from '../../../utils/getCreatedUpdatedColumns.utils';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Popover,
  Tag,
  Tooltip
} from 'antd';
import { selectListItems } from '../../../../redux/users/selectors';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  StopOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import LevelChip, { 
  LEVEL_LABELS, 
  type Level 
} from '../../../layout/context/LevelChip';
import Presence from '../../../UI/Presence';
import RoleBadge, { RolesGlossary } from '../../../layout/context/RoleBadge';
import type { 
  Administrability 
} from '../../../../service/userLifecycle.service';

const escapeRegExp =(text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The 2FA verdict, from the authenticator app (Keycloak TOTP), the only second factor.
 * "Unknown" is its own state: a factor that has not been read is not the same as one that
 * is off, and drawing it as "None" would tell an administrator an account has no second
 * factor when it may well have one.
 */
const TwoFactorTag = ({ totp }: { totp?: boolean }) => {
  const detail = totp === undefined
    ? 'Authenticator app: not read for this account.'
    : `Authenticator app: ${totp ? 'enrolled' : 'not enrolled'}.`;
  let tag = <Tag>Unknown</Tag>;
  if (totp) {
    tag = <Tag color="green">On</Tag>;
  } else if (totp === false) {
    tag = <Tag>None</Tag>;
  }
  return (
    <Tooltip title={detail}>
      {tag}
    </Tooltip>
  );
};

const columnHelper = createColumnHelper<UserType>();

/**
 * The roles cell. `viewLevel` is the level of the console being looked at: the role held
 * there is the one shown in the cell, and everything else folds into a muted "+N other
 * roles" that opens the full list with the node each role is on.
 */
export const rolesColumn = (viewLevel?: Level) => columnHelper.accessor('roles', {
  header: () => <span>Roles<RolesGlossary /></span>,
  enableHiding: false,
  size: 30,
  cell: props => {
    const { roles, memberships } = props.row.original;
    const more = (count: number, content: React.ReactNode) => (
      <Popover title="Other roles" content={content}>
        <span
          className="text-gray-500 text-xs cursor-default whitespace-nowrap"
          onClick={event => event.stopPropagation()}
        >
          +{count} other {count === 1 ? 'role' : 'roles'}
        </span>
      </Popover>
    );
    if (memberships && memberships.length > 0) {
      const found = memberships.findIndex(m => m.node_kind === viewLevel);
      const mainIndex = found < 0 ? 0 : found;
      const main = memberships[mainIndex];
      const others = memberships.filter((_, i) => i !== mainIndex);
      return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <RoleBadge
            role={main.role}
            level={main.node_kind as Level}
            nodeName={main.node_name}
          />
          {others.length > 0 && more(
            others.length,
            <div className="flex flex-col gap-1">
              {others.map(m => (
                <span
                  key={`${m.node_id}-${m.role}`}
                  className="flex items-center gap-1"
                >
                  <LevelChip level={m.node_kind as Level} compact />
                  <span>{m.role ?? 'custom'}</span>
                  <span className="text-gray-400">@ {m.node_name}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (roles.length === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <RoleBadge role={roles[0]} />
        {roles.length > 1 && more(
          roles.length - 1,
          <div className="flex flex-col gap-1">
            {roles.slice(1).map(role => <RoleBadge key={role} role={role} />)}
          </div>
        )}
      </div>
    );
  }
});

export const UserConfig: any = {
  columns: [
    columnHelper.accessor('email', {
      header: 'Person',
      enableHiding: false,
      size: 30,
      cell: props => {
        const { result: listResult } = useSelector(selectListItems);
        const searchTerm = listResult?.search?.trim() || '';
        const { email, firstName, lastName } = props.row.original;
        const fullName = [firstName, lastName].filter(Boolean).join(' ');

        const highlightText = (text = '', highlight = '') => {
          if (!highlight) return text;
          const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'));
          return (
            <span>
              {parts.map((part, index) =>
                part.toLowerCase() === highlight.toLowerCase() ? (
                  <span 
                    key={index} 
                    style={{ backgroundColor: 'yellow' }}
                  >{part}</span>
                ) : (
                  part
                )
              )}
            </span>
          );
        };
        return (
          <div className="max-w-[400px] py-1">
            {fullName && (
              <div 
                className="font-bold truncate"
              >{highlightText(fullName, searchTerm)}</div>
            )}
            <div 
              className={`truncate ${fullName ? 'text-gray-500 text-xs' : 'font-bold'}`}
            >
              {highlightText(email, searchTerm)}
            </div>
          </div>
        );
      }
    }), columnHelper.accessor('enabled', {
      header: 'Status',
      enableHiding: false,
      size: 30,
      cell: props => {
        const { inherited, roles, enabled, invitation } = props.row.original;
        if (invitation) {
          const when = new Date(invitation.expiresAt).toLocaleString();
          return invitation.expired
            ? <Tooltip title={`Expired ${when}`}><Tag>Expired</Tag></Tooltip>
            : <Tooltip title={`Expires ${when}`}>
              <Tag color="gold">Invited</Tag>
            </Tooltip>;
        }
        if (inherited) {
          const level = LEVEL_LABELS[inherited.from as Level] ?? inherited.from;
          return (
            <Tooltip
              title={inherited.manageable
                ? `Has access through ${roles[0]} on ${inherited.nodeName ?? 'an administrator above'}. You can change it because you administer ${inherited.nodeName ?? 'it'}.`
                : `Has access through ${roles[0]} on ${inherited.nodeName ?? 'an administrator above'}. Change it where it was granted.`}
            >
              <Tag icon={<LockOutlined />}>Inherited from {level}</Tag>
            </Tooltip>
          );
        }
        return enabled
          ? <Tag color="green">Active</Tag>
          : <Tag color="red">Suspended</Tag>;
      }
    }), columnHelper.display({
      id: 'lastLoginAt',
      header: 'Activity',
      size: 40,
      cell: props => {
        const { inherited, invitation, lastLoginAt, lastSeenAt } = props.row.original;
        if (inherited || invitation) return <span 
          className="text-gray-400"
        >-</span>;
        return <Presence lastSeenAt={lastSeenAt} lastLoginAt={lastLoginAt} />;
      }
    }), columnHelper.display({
      id: 'twoFactor',
      header: '2FA',
      size: 30,
      cell: props => props.row.original.invitation
        ? <span className="text-gray-400">-</span>
        : (
          <TwoFactorTag totp={props.row.original.totpEnabled} />
        )
    }), rolesColumn()
  ],

  visibility: {
    // control visible columns here if needed
  }
};

/**
 * The last column: inline icon buttons per row. Built where the handlers live because it
 * closes over the caller's tier rule and the console's node. A row the caller may not
 * administer only offers Manage; the server still decides every action.
 */
export const actionsColumn = ({
  administrabilityOf,
  allowSuspend = true,
  nodeLabel,
  canManageInherited = false,
  onManage,
  onToggleActive,
  onRemove,
  onResendInvitation,
  onRevokeInvitation
}: {
  administrabilityOf: (row: UserType) => Administrability;
  /** False at workspace level: suspending an account is an organisation or entity action. */
  allowSuspend?: boolean;
  nodeLabel: string;
  canManageInherited?: boolean;
  onManage: (row: UserType) => void;
  onToggleActive: (row: UserType) => void;
  onRemove: (row: UserType) => void;
  onResendInvitation?: (row: UserType) => void;
  onRevokeInvitation?: (row: UserType) => void;
}) => {
  const iconButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    danger = false
  ) => (
    <Tooltip title={label} key={label}>
      <Button
        type="text"
        size="small"
        danger={danger}
        icon={icon}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      />
    </Tooltip>
  );
  return columnHelper.display({
    id: 'actions',
    header: () => <div className="text-center">Actions</div>,
    size: 110,
    cell: props => {
      const row = props.row.original;
      if (row.inherited && !canManageInherited) return <div 
        className="text-center text-gray-400"
      >-</div>;
      let buttons: React.ReactNode[];
      if (row.invitation) {
        buttons = [
          iconButton(
            row.invitation.expired ? 'Renew and resend' : 'Resend',
            <MailOutlined />,
            () => onResendInvitation?.(row)
          ), iconButton('Revoke invitation', <CloseCircleOutlined />, () => onRevokeInvitation?.(row), true)
        ];
      } else {
        const { allowed, reason } = administrabilityOf(row);
        buttons = [
          iconButton(
            allowed ? 'Manage' : (reason || 'Manage'),
            <EditOutlined />,
            () => onManage(row)
          ), ...(allowed ? [
            ...(allowSuspend ? [iconButton(
              row.enabled ? 'Suspend account' : 'Reactivate account',
              row.enabled ? <StopOutlined /> : <CheckCircleOutlined />,
              () => onToggleActive(row),
              row.enabled
            )] : []), iconButton(
              `Remove from ${row.inherited?.nodeName ?? nodeLabel}`,
              <UserDeleteOutlined />,
              () => onRemove(row),
              true
            )
          ] : [])
        ];
      }
      // The row itself opens the dialog; this cell must not.
      return (
        <div 
          className="flex justify-center" 
          onClick={(event) => event.stopPropagation()}
        >
          {buttons}
        </div>
      );
    }
  });
};
